import React from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Skeleton } from "primereact/skeleton";
import PropTypes from "prop-types";
import ReactCountryFlag from "react-country-flag";
import CustomCheckBox from "../../../components/CheckBox/index";

const ServiceAccessTable = ({
  entityaccess,
  allTenantData,
  paginationOnchange,
  paginationFirst,
  paginationRows,
  role,
  loading,
}) => {
  const isGxmAdmin = role === "GXM_ADMIN" || role === "SUPER_ADMIN";
  const isCustomer = role === "CUSTOMER" || role === "CUSTOMER_MANAGER";

  const buildEntityNode = (entity) => ({
    key: `entity-${entity.id}`,
    data: {
      name: (
        <span>
          <ReactCountryFlag countryCode={entity.country} svg className="mr-2" />
          {`${entity.name} (${entity.country})`}
        </span>
      ),
      status: null,
    },
    children: entity.services?.map((service) => {
      const children = service.process_templates?.map((process) => ({
        key: `process-${process.id}`,
        data: {
          name: process.name,
          status: (
            <div className="flex text-center checkbox-margin">
              <CustomCheckBox inputId={`checkbox-${process.id}`} checked />
            </div>
          ),
        },
      }));

      return {
        key: `service-${service.id}`,
        data: {
          name: service.name,
          status: null,
        },
        children,
      };
    }),
  });

  const convertToTreeNodes = (data) => {
    // Customer User/Manager: flat entity list (no tenant grouping)
    // Entity → Service → Process Templates
    if (isCustomer) {
      return data.map((entity) => buildEntityNode(entity));
    }

    // Group entities by tenant
    const tenantMap = {};

    data.forEach((entity) => {
      const tenantId = entity.tenant_id || "unknown";
      const tenantData = entity.tenantData || {
        id: tenantId,
        tenant_name: "Unknown Tenant",
      };

      if (!tenantMap[tenantId]) {
        tenantMap[tenantId] = {
          id: tenantId,
          name: tenantData.tenant_name || "Unknown Tenant",
          data: tenantData,
          entities: [],
        };
      }
      tenantMap[tenantId].entities.push(entity);
    });

    // GXM Admin / Super Admin: show tenant list from allTenantData
    if (isGxmAdmin) {
      if (allTenantData && allTenantData.length > 0) {
        return allTenantData.map((tenant) => ({
          key: `tenant-${tenant.id}`,
          data: {
            name: <span>{tenant.name}</span>,
            status: null,
          },
        }));
      }
      // Fallback: derive from entity access if allTenantData is not available
      return Object.values(tenantMap).map((tenant) => ({
        key: `tenant-${tenant.id}`,
        data: {
          name: <span>{tenant.name}</span>,
          status: null,
        },
      }));
    }

    // LSP User/Manager & GXM User/Manager: full tree with tenants → entities → services → process templates
    return Object.values(tenantMap).map((tenant) => ({
      key: `tenant-${tenant.id}`,
      data: {
        name: <span>{tenant.name}</span>,
        status: null,
      },
      children: tenant.entities.map((entity) => buildEntityNode(entity)),
    }));
  };

  const skeletonNodes = [
    {
      key: "skeleton-0",
      data: {
        name: (
          <div>
            <Skeleton width="60%" height="1.2rem" />
          </div>
        ),
      },
    },
  ];

  const treeNodes = loading
    ? skeletonNodes
    : convertToTreeNodes(entityaccess || []);

  return (
    <div className="user-access-table">
      <TreeTable
        value={treeNodes}
        emptyMessage="No results were found"
        responsiveLayout="scroll"
        paginator={!loading && entityaccess?.length > 10}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[10, 20, 30]}
        first={paginationFirst}
        rows={paginationRows}
        onPage={paginationOnchange}
      >
        <Column
          field="name"
          header={
            isGxmAdmin ? (
              <span>
                Account Name{" "}
                <i
                  className="pi pi-info-circle info-icon"
                  id="gxm-admin-myaccount-info"
                />
                <Tooltip
                  target="#gxm-admin-myaccount-info"
                  content="GXM Admin users inherit full visibility across all entities, processes, and services within the tenant."
                  position="right"
                  className="gxm-admin-tooltip"
                />
              </span>
            ) : (
              "Account Name"
            )
          }
          expander={!isGxmAdmin}
          className="table-entity-column"
        />
      </TreeTable>
    </div>
  );
};

ServiceAccessTable.propTypes = {
  entityaccess: PropTypes.array,
  allTenantData: PropTypes.array,
  paginationFirst: PropTypes.number,
  paginationRows: PropTypes.number,
  paginationOnchange: PropTypes.func,
  role: PropTypes.string,
  loading: PropTypes.bool,
};
export default ServiceAccessTable;
